import { Chat, Message, ChatTurnResult, HitlAction } from "./type/chat";
import { TaskListItem } from "./type/mq-task";
import { LogAdapter } from "./adapter/log";
import { LogDownloadResult, UpdatedAtTz } from "./type/log";
import { TError } from "../../types/error";
import { ChatAdapter } from "./adapter/chat";
import { IAssistantRepo, RagStreamRepoHandlers } from "./irepo";
import { parseAssistantCapabilities } from "./adapter/assistant-transport-policy";
import { AssistantCapabilities } from "./type/capabilities";
import {
  applyRagStreamTerminalEvent,
  requireRagStreamTerminal,
} from "./adapter/rag-stream-terminal";
import { RagStreamEvent } from "./type/rag-stream-event";
import { RagStreamNotSupportedError } from "./sse-client";

export type RagStreamCallbacks = {
  onEvent?: (event: RagStreamEvent) => void;
  signal: AbortSignal;
};

export class AssistantUsecase {
  private repo: IAssistantRepo;
  private error: (error: TError) => void;

  constructor(repo: IAssistantRepo, error: (error: TError) => void) {
    this.repo = repo;
    this.error = error;
  }

  async addChat(req: {
    chatId: string;
    chatNo: number;
    query: string;
    modelId: string;
    mode: string;
    files?: File[];
  }): Promise<ChatTurnResult> {
    try {
      const dto = ChatAdapter.toInputDTO(req);
      const data = await this.repo.sendMessage(dto);
      return ChatAdapter.toAddChatOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async addChatStream(
    req: {
      chatId: string;
      chatNo: number;
      query: string;
      modelId: string;
      mode: string;
      files?: File[];
    },
    callbacks: RagStreamCallbacks,
  ): Promise<ChatTurnResult> {
    const dto = ChatAdapter.toInputDTO(req);
    return this.runRagStream(
      (handlers) => this.repo.sendMessageStream(dto, handlers),
      () => this.addChat(req),
      callbacks,
    );
  }

  async resumeHitl(req: {
    chatId: string;
    modelId: string;
    action: HitlAction;
    feedback?: string;
    runId?: string;
  }): Promise<ChatTurnResult> {
    try {
      const dto = ChatAdapter.toResumeHitlInputDTO(req);
      const data = await this.repo.resumeHitl(dto);
      return ChatAdapter.toAddChatOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async resumeHitlStream(
    req: {
      chatId: string;
      modelId: string;
      action: HitlAction;
      feedback?: string;
      runId?: string;
    },
    callbacks: RagStreamCallbacks,
  ): Promise<ChatTurnResult> {
    const dto = ChatAdapter.toResumeHitlInputDTO(req);
    return this.runRagStream(
      (handlers) => this.repo.resumeHitlStream(dto, handlers),
      () => this.resumeHitl(req),
      callbacks,
    );
  }

  async fetchCapabilities(): Promise<AssistantCapabilities> {
    try {
      const data = await this.repo.getCapabilities();
      return parseAssistantCapabilities(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchHistory(): Promise<Chat[]> {
    try {
      const data = await this.repo.getHistory();
      return ChatAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchChat(chatId: string): Promise<Message[]> {
    try {
      const data = await this.repo.getChat(chatId);
      return ChatAdapter.toChatOutputDTO(data, chatId);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async deleteChat(chatId: string): Promise<any> {
    try {
      const data = await this.repo.deleteChat(chatId);
      return data;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async updateChatTitle(chatId: string, title: string): Promise<any> {
    try {
      const data = await this.repo.updateChatTitle(chatId, { title });
      return data;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** main 版アシスタント（_legacy）が使う。develop-langgraph 版は SSE を使う */
  async fetchTaskList(chatIds: string[]): Promise<TaskListItem[]> {
    try {
      return await this.repo.fetchTaskList(chatIds);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  /** main 版アシスタント（_legacy）が使う。develop-langgraph 版は SSE を使う */
  async ackTasks(taskIds: string[]): Promise<string[]> {
    try {
      return await this.repo.ackTasks(taskIds);
    } catch (error) {
      // 後始末の失敗は体験上無害なので、トーストに流さずポーリングを継続する
      console.error("Failed to ack tasks:", error);
      return [];
    }
  }

  /** 指定した日が属する月（1日〜末日）の利用ログ CSV を取得する */
  async downloadMonthlyLog(
    base: Date = new Date(),
    updatedAtTz: UpdatedAtTz = "utc"
  ): Promise<LogDownloadResult> {
    const req = LogAdapter.toInputDTO(base, updatedAtTz);
    try {
      const blob = await this.repo.downloadLog(req);
      return { blob, fileName: LogAdapter.toFileName(req) };
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async downloadArtifact(uri: string, fileExtension: string): Promise<any> {
    try {
      return await this.repo.downloadArtifact(uri, fileExtension);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  private async runRagStream(
    streamFn: (handlers: RagStreamRepoHandlers) => Promise<void>,
    fallback: () => Promise<ChatTurnResult>,
    callbacks: RagStreamCallbacks,
  ): Promise<ChatTurnResult> {
    const terminal = { current: null as ChatTurnResult | null };
    const handlers: RagStreamRepoHandlers = {
      signal: callbacks.signal,
      onEvent: (event) => {
        callbacks.onEvent?.(event);
        applyRagStreamTerminalEvent(event, terminal);
      },
    };

    try {
      await streamFn(handlers);
    } catch (error) {
      if (error instanceof RagStreamNotSupportedError) {
        return fallback();
      }
      this.error({ id: null, error });
      throw error;
    }

    return requireRagStreamTerminal(terminal);
  }
}
